import { useEffect, useRef } from 'react';
import { setCleverTapConsentGate, isCleverTapPresent } from '@lib/clevertap';

interface CleverTapConsentRevocationArgs {
  /** True once the CleverTap web SDK has finished init(). */
  clevertapReady: boolean;
  /** True once the initial CookieYes read has completed (consent.hasChecked). */
  consentChecked: boolean;
  /** Whether the category governing CleverTap is granted (consent.clevertap). */
  consentGranted: boolean;
}

/**
 * Keeps CleverTap in step with CookieYes WITHOUT ever writing to the user's
 * CleverTap profile (FFH-2334).
 *
 * This replaces `useCleverTapConsentSync`, which pushed consent into the SDK
 * via `privacy.push({ optOut: !granted })`. That call is not a local flag: it
 * writes the `ct_optout` system property to the profile server-side, and
 * CleverTap then filters that profile out of every segment calculation and
 * sends it no campaigns even when it qualifies. A cookie preference must never
 * be recorded as the user withdrawing from data processing, so the app no
 * longer pushes `optOut` at all — in either direction.
 *
 * What replaces it:
 *
 * - GRANT needs no action. Consent now gates `init()` itself (see
 *   `AppInitializers`), so the SDK simply does not exist without it, and the
 *   SDK's own default is opted in. There is nothing left to push.
 *
 * - WITHDRAWAL mid-session is the one case needing work, because by then the
 *   SDK is already live and auto-tracking page views (`init()` calls
 *   `pageChanged()`). The SDK offers no supported way to shut down quietly —
 *   `privacy.push({ optOut: true })` is the only lever it exposes, and that is
 *   exactly the profile write we are eliminating. So instead we close a local
 *   gate (stopping every app-initiated call immediately) and reload, after
 *   which the SDK is never initialised again.
 *
 * Note `WZRK_*` storage is deliberately left in place on withdrawal: it is
 * inert once the SDK is not initialised, and clearing it would sever the
 * device-to-profile link, fragmenting anonymous users into a fresh profile on
 * any later re-accept.
 */
export const useCleverTapConsentRevocation = ({
  clevertapReady,
  consentChecked,
  consentGranted
}: CleverTapConsentRevocationArgs) => {
  // Tracks the previous grant so we can distinguish a genuine withdrawal from
  // the unresolved DENIED default that every session starts on.
  const wasGrantedRef = useRef(false);

  useEffect(() => {
    if (!consentChecked) return;

    setCleverTapConsentGate(consentGranted);

    const wasGranted = wasGrantedRef.current;
    wasGrantedRef.current = consentGranted;

    // Only a true granted -> denied transition is a withdrawal. Reloading is
    // gated on the SDK actually being live, so a user who arrives with consent
    // already denied (the common case) can never be caught in a reload loop.
    //
    // "Live" deliberately means PRESENT, not ready: `clevertapReady` trails
    // `init()` by the readiness poll, and a withdrawal inside that window would
    // otherwise be missed for good — this edge is consumed on the render that
    // sees it, so a later `clevertapReady` flip never re-triggers it. The SDK
    // would then keep auto-tracking page views, which the gate cannot stop.
    const sdkIsLive = clevertapReady || isCleverTapPresent();

    if (wasGranted && !consentGranted && sdkIsLive) {
      window.location.reload();
    }
  }, [clevertapReady, consentChecked, consentGranted]);
};
